import React, { useState } from 'react';
import { Box, TextField, Button, Typography, IconButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import BottomSheetDialog from './mobile-bottom-sheet';
import DeletePopupMobile from './delete-mobile';
import Dropdown, { DropdownOption } from '../../ui/dropdown';
import TreeDropdown, { TreeDropdownOption } from '../../tree-dropdown';
import OneTimeProvision from '../parts/one-time-provision';
import AddBankAccount from '../add-bank-account';
import type { EditableTransaction } from '../add-transaction';

import useApi from '../../../hooks/use-api';
import buildApiRequestConfig from '../../../api/url-builder';
import routes from '../../../api/routes';
import { useUser } from '../../../providers/user-provider';
import { usePermissions } from '../../../providers/permissions-provider';

interface Transaction {
    amount: string;
    description: string;
    transactionDate: string;
    categoryId?: number;
    bankAccountId?: number;
}

interface AddTransactionPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: Transaction) => void;
    isMobile?: boolean;
    // Existing manually-added transaction being edited - see add-transaction.tsx.
    editTransaction?: EditableTransaction | null;
}

// Bottom-sheet phone equivalent of add-transaction.tsx - same
// validation/change-tracking/bank-creation logic, laid out as a single
// scrollable column instead of a two-field row.
const AddTransactionPopupMobile: React.FC<AddTransactionPopupProps> = ({ open, onClose, onCreate, editTransaction = null }) => {
    const { user } = useUser();
    const { can } = usePermissions();
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [category, setCategory] = useState('');
    const [bank, setBank] = useState('');
    const [categoryOptions, setCategoryOptions] = useState<TreeDropdownOption[]>([]);
    const [bankOptions, setBankOptions] = useState<DropdownOption[]>([]);
    const [openModal, setOpenModal] = useState<'cancel' | ''>('');
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [errors, setErrors] = useState<{ amount?: string, description?: string, date?: string }>({});
    const [initialState, setInitialState] = useState({ amount: '', description: '', date: '', category: '', bank: '' });

    const [fetchCategories, setFetchCategories] = useApi();
    const [fetchBanks, setFetchBanks] = useApi();
    const [createBanks, setCreateBanks] = useApi();

    React.useEffect(() => {
        if (!user) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchBanks({ url, method });
        const { url: catUrl, method: catMethod } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchCategories({ url: catUrl, method: catMethod });
    }, [open, user, setFetchBanks, setFetchCategories]);

    React.useEffect(() => {
        if (open) {
            // Blank form for a new transaction, or the existing values when editing
            const initial = {
                amount: editTransaction ? String(editTransaction.amount) : '',
                description: editTransaction?.description ?? '',
                date: editTransaction ? String(editTransaction.date).slice(0, 10) : '',
                category: editTransaction?.categoryId ? String(editTransaction.categoryId) : '',
                bank: editTransaction?.bankAccountId ? String(editTransaction.bankAccountId) : '',
            };
            setAmount(initial.amount);
            setDescription(initial.description);
            setDate(initial.date);
            setCategory(initial.category);
            setBank(initial.bank);
            setErrors({});
            setInitialState(initial);
        }
    }, [open, editTransaction]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (data && !isLoading) {
            const transformCategory = (cat: any): TreeDropdownOption => ({
                id: cat.id.toString(),
                label: cat.name,
                children: cat.children ? cat.children.map(transformCategory) : undefined
            });
            setCategoryOptions(data.map(transformCategory));
        }
        if (error) console.error('Error fetching categories:', error);
    }, [fetchCategories]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchBanks;
        if (data && !isLoading) {
            const filteredBanks = data.filter((a: any) => a.bank.toLowerCase() === 'cash').map((item: any) => ({ value: String(item.id), label: `${item.bank} (${item.name})` }));
            const banksWithCreate: DropdownOption[] = [
                ...(can('transactions.addBankAccount', 'create') ? [{ value: '__add_new__', label: '+ Add New Bank Account' }] : []),
                ...filteredBanks
            ];
            setBankOptions(banksWithCreate);
            // Not when editing - that would silently change the saved bank.
            if (filteredBanks.length === 1 && !editTransaction) {
                const selectedValue = filteredBanks[0].value;
                setBank(selectedValue);
                setInitialState(prev => ({ ...prev, bank: selectedValue }));
            }
        }
        if (error) console.error('Error fetching banks:', error);
    }, [fetchBanks]);

    React.useEffect(() => {
        const { isLoading } = createBanks;
        if (!isLoading && showAddBankModal === false) {
            if (!user) return;
            const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchBanks({ url, method });
        }
    }, [createBanks, showAddBankModal, user, setFetchBanks]);

    const validate = () => {
        const newErrors: { amount?: string, description?: string, date?: string } = {};
        if (!amount) newErrors.amount = 'Amount is required';
        if (!description) newErrors.description = 'Description is required';
        if (!date) newErrors.date = 'Date is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => (
        amount !== initialState.amount ||
        description !== initialState.description ||
        date !== initialState.date ||
        category !== initialState.category ||
        bank !== initialState.bank
    );

    const handleCreate = () => {
        setErrors({});
        if (validate()) {
            onCreate({
                amount,
                description,
                transactionDate: date,
                bankAccountId: bank ? Number(bank) : undefined,
                categoryId: category ? Number(category) : undefined
            });
        }
    };

    const handleClose = () => {
        setAmount('');
        setDescription('');
        setDate('');
        setCategory('');
        setBank('');
        setErrors({});
        setOpenModal('');
        onClose();
    };

    const handleBankCreated = () => {
        if (!user) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setCreateBanks({ url, method });
        setShowAddBankModal(false);
    };

    return (
        <BottomSheetDialog open={open} onClose={() => {}}>
            <AddBankAccount
                open={showAddBankModal}
                onClose={() => setShowAddBankModal(false)}
                onCreate={handleBankCreated}
                bankFilter={(bank: any) => bank.name && bank.name.toLowerCase() === 'cash'}
            />
            <DeletePopupMobile
                open={openModal === 'cancel'}
                message="Discard Changes"
                description="All changes made in this form will be lost if you proceed"
                onCancel={() => setOpenModal('')}
                onConfirm={handleClose}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <IconButton onClick={() => (hasChanges() ? setOpenModal('cancel') : handleClose())}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>{editTransaction ? 'Edit Transaction' : 'Add New Transaction'}</Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Transaction Amount <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Amount"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        error={!!errors.amount}
                        helperText={errors.amount || 'Negative for expenses, positive for income.'}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Description <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Description"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        error={!!errors.description}
                        helperText={errors.description}
                        sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                    />
                </Box>
                <OneTimeProvision date={date} setDate={setDate} errorDate={!!errors.date} helperText={errors.date} />
                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>Category</Typography>
                    <TreeDropdown
                        options={categoryOptions}
                        value={category}
                        onChange={(selectedId) => setCategory(selectedId || '')}
                        placeholder="Choose Category (Optional)"
                        height={40}
                        width="100%"
                    />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 0.5, color: 'var(--color-text-muted)' }}>Bank Account</Typography>
                    <Dropdown
                        options={bankOptions}
                        value={bank}
                        onChange={(val) => {
                            if (val === '__add_new__') setShowAddBankModal(true);
                            else setBank(val);
                        }}
                        placeholder="Choose Bank Account (Optional)"
                        height={40}
                    />
                </Box>
            </Box>
            <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, display: 'flex', gap: 1, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                <Button
                    onClick={() => (hasChanges() ? setOpenModal('cancel') : handleClose())}
                    variant="outlined"
                    fullWidth
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    fullWidth
                    sx={{ backgroundColor: 'var(--color-primary)', color: '#fff', '&:hover': { backgroundColor: 'var(--color-primary)' } }}
                >
                    {editTransaction ? 'Save Changes' : 'Add Transaction'}
                </Button>
            </Box>
        </BottomSheetDialog>
    );
};

export default AddTransactionPopupMobile;
